import { Injectable, DOCUMENT, inject } from '@angular/core';
import { AlertController, IonicSafeString } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { from, interval, Observable, Subject } from 'rxjs';
import {
  distinctUntilChanged,
  map,
  switchMap,
  take,
  takeUntil,
  tap
} from 'rxjs/operators';
import { CoreActionCheckBatteryComplete } from '../core.actions';
import { BatteryUtilsService } from './battery-utils.service';

@Injectable({
  providedIn: 'root'
})
export class BatteryAlertService {
  private alertController = inject(AlertController);
  private translate = inject(TranslateService);
  private batteryUtilsService = inject(BatteryUtilsService);
  private document = inject<HTMLDocument>(DOCUMENT);

  private readonly iconContainerId = 'battery-icon-container';
  private readonly transitionDuration = 400;

  /**
   * Creates and presents a low battery alert with dynamic icon updates.
   * @param appColor Color for the alert styling
   * @param isCharging Initial charging status
   * @returns Observable that emits action when alert is dismissed
   */
  showBatteryAlert(
    appColor: string,
    isCharging: boolean
  ): Observable<CoreActionCheckBatteryComplete> {
    const initialIcon = this.getBatteryIcon(isCharging);

    return from(
      this.alertController.create({
        mode: 'ios',
        cssClass: 'bepatient-shared-alert battery-check-alert',
        backdropDismiss: false,
        message: new IonicSafeString(
          `<div id="${this.iconContainerId}" class="low-battery-icon-container" style="background:${appColor}">${initialIcon}</div> <br> ` +
            this.translate.instant('LOW_BATTERY_MESSAGE')
        ),
        buttons: [
          {
            text: this.translate.instant('OK'),
            handler: () => true
          }
        ]
      })
    ).pipe(
      switchMap(alert =>
        from(alert.present()).pipe(
          switchMap(() => this.setupBatteryStatusPolling(alert))
        )
      )
    );
  }

  private setupBatteryStatusPolling(
    alert: HTMLIonAlertElement
  ): Observable<CoreActionCheckBatteryComplete> {
    const alertDismissed$ = new Subject<void>();

    alert.onDidDismiss().then(() => {
      alertDismissed$.next();
      alertDismissed$.complete();
    });

    const batteryPoll$ = interval(1000).pipe(
      switchMap(() => from(this.batteryUtilsService.checkBattery(true))),
      distinctUntilChanged((prev, curr) => prev.isCharging === curr.isCharging),
      tap(batteryInfo => this.updateBatteryIcon(batteryInfo.isCharging)),
      takeUntil(alertDismissed$)
    );

    batteryPoll$.subscribe();

    return alertDismissed$.pipe(
      take(1),
      map(() => new CoreActionCheckBatteryComplete(true))
    );
  }

  private updateBatteryIcon(isCharging: boolean): void {
    const iconContainer = this.document.getElementById(this.iconContainerId);
    if (!iconContainer) {
      return;
    }

    const currentIcon = iconContainer.querySelector('.low-battery-charging');
    const isCurrentlyCharging = !!currentIcon;

    if (isCharging === isCurrentlyCharging) {
      return;
    }

    iconContainer.classList.remove('icon-fade-in');
    iconContainer.classList.add('icon-fade-out');

    setTimeout(() => {
      iconContainer.innerHTML = this.getBatteryIcon(isCharging);
      iconContainer.classList.remove('icon-fade-out');
      iconContainer.classList.add('icon-fade-in');
    }, this.transitionDuration);
  }

  private getBatteryIcon(isCharging: boolean): string {
    return isCharging
      ? '<ion-icon name="battery-charging" class="low-battery-icon low-battery-charging"></ion-icon>'
      : '<i class="fa-solid fa-battery-quarter low-battery-icon"></i>';
  }
}
